import { CommandProps, getMarkRange, getMarkType } from '@tiptap/core';
import TiptapLink from '@tiptap/extension-link';
import { EditorView, TextSelection, Plugin } from './proseMirrorTypes';

export const selectWholeLink = (
  pluginName: string,
  view: EditorView,
  pos: number
) => {
  const { schema, doc, tr } = view.state;

  const type = getMarkType(pluginName, schema);
  const range = getMarkRange(doc.resolve(pos), type);

  if (!range) {
    return;
  }

  const { from, to } = range;
  const start = Math.min(from, to);
  const end = Math.max(from, to);

  if (pos < start || pos > end) {
    return;
  }

  const $start = doc.resolve(start);
  const $end = doc.resolve(end);
  const transaction = tr.setSelection(new TextSelection($start, $end));

  view.dispatch(transaction);
};

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    extendedLink: {
      updateLink: (href: string, text?: string) => ReturnType;
    };
  }
}

type LinkOptions = {
  includePresentationLinks?: boolean;
};

export const Link = TiptapLink.extend<LinkOptions>({
  inclusive: false,

  parseHTML() {
    if (this.options.includePresentationLinks === false) {
      return [
        {
          tag: 'a[href]:not([href^="/api/attachment"]):not([href*="ardoq.com/presentation"]):not([href*="ardoq.dev/presentation"])',
        },
      ];
    }

    return [
      {
        tag: 'a[href]:not([href^="/api/attachment"])',
      },
    ];
  },

  // The current paste rules are a bit too permissive, so we're disabling them.
  // Links will still be detected by the autoLink option, which seems to be more strict.
  addPasteRules() {
    return [];
  },

  addOptions() {
    return {
      ...this.parent?.(),
      openOnClick: false,
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      updateLink:
        (href: string, text?: string) =>
        ({ chain }: CommandProps) => {
          return chain()
            .focus()
            .extendMarkRange('link')
            .insertContent({
              type: 'text',
              text: text || href,
              marks: [{ type: 'link', attrs: { href, target: '_blank' } }],
            })
            .setLink({ href })
            .run();
        },
    };
  },

  addProseMirrorPlugins() {
    const { editor, name } = this;

    const plugins = this.parent?.() || [];

    return [
      ...plugins,
      new Plugin({
        props: {
          // Handle 'Escape' key press to move cursor to end of link
          // when it is selected.
          handleKeyDown: (_: EditorView, event: KeyboardEvent) => {
            const { selection } = editor.state;
            if (event.key === 'Escape' && selection.empty !== true) {
              editor.commands.focus(selection.to, { scrollIntoView: false });
            }

            return false;
          },
          // Select the whole link when clicked
          handleClick(view, pos) {
            selectWholeLink(name, view, pos);
          },
        },
      }),
    ];
  },
});
